import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Search, User, Plus, Check } from 'lucide-react';
import { useSocialStore } from '../store/socialStore';
import { useFriendStore } from '../store/friendStore';
import { Friend } from '../types/social';

interface ShareRecipeModalProps {
  recipeId: string;
  recipeTitle: string;
  recipeImage?: string;
  onClose: () => void;
  recipeUrl: string;
}

export function ShareRecipeModal({
  recipeId,
  recipeTitle,
  recipeImage,
  onClose,
  recipeUrl
}: ShareRecipeModalProps) {
  const [message, setMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<Friend[]>([]);
  const [showFriendSearch, setShowFriendSearch] = useState(false);
  const [scale, setScale] = useState(1);
  const modalRef = useRef<HTMLDivElement>(null);

  const { createPost } = useSocialStore();
  const { friends, fetchFriends } = useFriendStore();

  useEffect(() => {
    fetchFriends();
  }, [fetchFriends]);

  const filteredFriends = friends.filter(
    (friend) =>
      !selectedFriends.some((selected) => selected.id === friend.id) &&
      friend.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    setSharing(true);
    try {
      // Uncomment and use your createPost call when ready:
      await createPost({
        content: message,
        recipeId,
        imageUrl: recipeImage,
        taggedUserIds: selectedFriends.map((friend) => friend.id),
        recipeUrl: recipeUrl 
      });
      onClose();
    } finally {
      setSharing(false);
    }
  };

  const toggleFriend = (friend: Friend) => {
    setSelectedFriends((current) =>
      current.some((f) => f.id === friend.id)
        ? current.filter((f) => f.id !== friend.id)
        : [...current, friend]
    );
  };

  // Adjust the modal scale so it never exceeds the viewport height
  const adjustModalScale = () => {
    if (modalRef.current) {
      const modalHeight = modalRef.current.offsetHeight;
      // Subtract a total margin of 32px (16px top and bottom)
      const availableHeight = window.innerHeight - 32;
      if (modalHeight > availableHeight) {
        setScale(availableHeight / modalHeight);
      } else {
        setScale(1);
      }
    }
  };

  useEffect(() => {
    adjustModalScale();
    window.addEventListener('resize', adjustModalScale);
    return () => window.removeEventListener('resize', adjustModalScale);
  }, []);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          style={{ transform: `scale(${scale})` }}
          className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Share Recipe</h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 flex-grow">
            <form onSubmit={handleShare} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipe
                </label>
                <div className="p-3 bg-gray-50 rounded-lg text-gray-700">
                  {recipeTitle}
                </div>
              </div>

              {recipeImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preview
                  </label>
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src={recipeImage}
                      alt={recipeTitle}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tag Friends
                </label>
                <div className="space-y-2">
                  {/* Selected friends */}
                  {selectedFriends.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedFriends.map((friend) => (
                        <span
                          key={friend.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                        >
                          <User size={14} />
                          {friend.email}
                          <button
                            type="button"
                            onClick={() => toggleFriend(friend)}
                            className="p-1 hover:bg-blue-100 rounded-full"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Friend search */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowFriendSearch(!showFriendSearch)}
                      className="w-full flex items-center gap-2 px-3 py-2 border rounded-lg text-left text-gray-600 hover:bg-gray-50"
                    >
                      <Plus size={20} />
                      Tag friends
                    </button>

                    {showFriendSearch && (
                      <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Search friends..."
                              className="w-full pl-8 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <Search
                              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                              size={16}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredFriends.map((friend) => (
                            <button
                              key={friend.id}
                              type="button"
                              onClick={() => toggleFriend(friend)}
                              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left"
                            >
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <User size={16} className="text-gray-500" />
                              </div>
                              <span className="flex-1 truncate">
                                {friend.email}
                              </span>
                              <Check size={16} className="text-blue-500 opacity-0" />
                            </button>
                          ))}
                          {filteredFriends.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                              No friends found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="share-message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Add a message
                </label>
                <textarea
                  id="share-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Say something about this recipe..."
                  maxLength={500}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
                <div className="mt-1 text-sm text-gray-500 text-right">
                  {message.length}/500 characters
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sharing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Send size={20} />
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
